/**
 * Bikini Bag - regalo automático por compra mínima
 * Shopify Online Store / Theme
 *
 * Regla:
 * - Subtotal mínimo: USD $500
 * - Regalo prioritario: Color 1 (variant ID 41316894507072)
 * - Regalo alternativo: Color 2 (variant ID 41316860723264)
 * - Agrega solamente una referencia.
 * - Si Color 1 no está disponible, intenta Color 2.
 * - Si el carrito baja de USD $500, elimina el regalo.
 *
 * IMPORTANTE:
 * 1. Mantén activo en Shopify el descuento automático "Buy X Get Y".
 * 2. Incluye ambas variantes como productos elegibles para el regalo.
 * 3. Configura 100% de descuento y máximo 1 uso por pedido.
 * 4. Desactiva Flash Free Gift Upsell para evitar duplicados.
 */

(function () {
  'use strict';

  // Evita que el archivo se inicialice dos veces.
  if (window.__BIKINI_FREE_GIFT_INITIALIZED__) return;
  window.__BIKINI_FREE_GIFT_INITIALIZED__ = true;

  /* =========================================================
     CONFIGURACIÓN
  ========================================================= */

  const CONFIG = {
    minimumAmount: 50000, // USD $500 expresados en centavos
    currency: 'USD',

    // Prioridad: primero Color 1; si no está disponible, Color 2.
    giftVariantIds: [
      41316894507072, // Color 1
      41316860723264  // Color 2
    ],

    giftPropertyName: '_automatic_bikini_gift',
    giftPropertyValue: 'spend_500',

    synchronizationDelay: 350,
    reloadAfterChange: true
  };

  /* =========================================================
     ESTADO INTERNO
  ========================================================= */

  const shopifyRoot =
    window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';

  const originalFetch = window.fetch.bind(window);

  let synchronizationTimer = null;
  let synchronizationRunning = false;
  let reloadScheduled = false;

  const giftVariantIds = CONFIG.giftVariantIds.map(Number);

  /* =========================================================
     UTILIDADES
  ========================================================= */

  function buildCartUrl(path) {
    return `${shopifyRoot}${path}`;
  }

  function isConfiguredGiftVariant(item) {
    return giftVariantIds.includes(Number(item.variant_id));
  }

  function getRequestPath(resource) {
    try {
      const value =
        typeof resource === 'string'
          ? resource
          : resource && resource.url
            ? resource.url
            : '';

      if (!value) return '';

      return new URL(value, window.location.origin).pathname;
    } catch (error) {
      return '';
    }
  }

  function isCartMutation(resource) {
    const path = getRequestPath(resource);

    return /\/cart\/(add|change|update|clear)\.js$/.test(path);
  }

  async function requestJson(url, options = {}) {
    const response = await originalFetch(url, options);

    let data = null;

    try {
      data = await response.json();
    } catch (error) {
      data = null;
    }

    if (!response.ok) {
      const requestError = new Error(
        (data && (data.description || data.message)) ||
        `Shopify cart request failed (${response.status})`
      );

      requestError.status = response.status;
      requestError.data = data;

      throw requestError;
    }

    return data;
  }

  /* =========================================================
     SHOPIFY CART API
  ========================================================= */

  function getCart() {
    return requestJson(buildCartUrl('cart.js'), {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      },
      credentials: 'same-origin'
    });
  }

  function addGift(variantId) {
    return requestJson(buildCartUrl('cart/add.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [
          {
            id: Number(variantId),
            quantity: 1,
            properties: {
              [CONFIG.giftPropertyName]: CONFIG.giftPropertyValue
            }
          }
        ]
      })
    });
  }

  function changeLineQuantity(lineKey, quantity) {
    return requestJson(buildCartUrl('cart/change.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: lineKey,
        quantity: Number(quantity)
      })
    });
  }

  /* =========================================================
     LÓGICA DEL REGALO
  ========================================================= */

  function getQualifyingSubtotal(cart) {
    return cart.items.reduce(function (subtotal, item) {
      // Las Bikini Bags configuradas no cuentan para llegar a los USD $500.
      if (isConfiguredGiftVariant(item)) return subtotal;

      /*
       * original_line_price calcula el subtotal antes de descuentos.
       * Esto normalmente coincide con la lógica de compra mínima
       * configurada en Buy X Get Y.
       */
      return subtotal + Number(item.original_line_price || 0);
    }, 0);
  }

  async function removeGiftLines(giftLines) {
    for (const giftLine of giftLines) {
      await changeLineQuantity(giftLine.key, 0);
    }
  }

  async function addFirstAvailableGift() {
    for (const variantId of giftVariantIds) {
      try {
        await addGift(variantId);

        console.info(
          `[Bikini Free Gift] Regalo agregado. Variant ID: ${variantId}`
        );

        return true;
      } catch (error) {
        /*
         * Shopify suele devolver 422 cuando la variante está agotada,
         * no está publicada o no puede agregarse al carrito.
         */
        if (error.status === 422) {
          console.warn(
            `[Bikini Free Gift] Variante no disponible: ${variantId}`,
            error.message
          );

          continue;
        }

        throw error;
      }
    }

    console.warn(
      '[Bikini Free Gift] Ninguna de las dos variantes está disponible.'
    );

    return false;
  }

  function notifyTheme() {
    const eventNames = [
      'cart:updated',
      'cart:refresh',
      'cart:change',
      'cart:build'
    ];

    eventNames.forEach(function (eventName) {
      document.dispatchEvent(
        new CustomEvent(eventName, {
          bubbles: true,
          detail: {
            source: 'bikini-free-gift'
          }
        })
      );
    });
  }

  function finishCartUpdate() {
    notifyTheme();

    if (CONFIG.reloadAfterChange && !reloadScheduled) {
      reloadScheduled = true;
      window.location.reload();
    }
  }

  async function synchronizeGift() {
    if (synchronizationRunning || reloadScheduled) return;

    synchronizationRunning = true;

    try {
      const cart = await getCart();

      if (
        CONFIG.currency &&
        cart.currency &&
        String(cart.currency).toUpperCase() !==
          String(CONFIG.currency).toUpperCase()
      ) {
        console.warn(
          `[Bikini Free Gift] La moneda del carrito es ${cart.currency}. ` +
          `La promoción está configurada para ${CONFIG.currency}.`
        );

        return;
      }

      const giftLines = cart.items.filter(isConfiguredGiftVariant);
      const qualifyingSubtotal = getQualifyingSubtotal(cart);
      const qualifies = qualifyingSubtotal >= CONFIG.minimumAmount;

      let cartChanged = false;

      // No cumple: elimina cualquiera de las dos referencias del regalo.
      if (!qualifies && giftLines.length > 0) {
        await removeGiftLines(giftLines);
        cartChanged = true;
      }

      // Cumple y no tiene regalo: intenta Color 1 y luego Color 2.
      if (qualifies && giftLines.length === 0) {
        cartChanged = await addFirstAvailableGift();
      }

      // Cumple y ya tiene regalo: conserva solo una referencia y una unidad.
      if (qualifies && giftLines.length > 0) {
        const retainedGift = giftLines[0];

        if (Number(retainedGift.quantity) !== 1) {
          await changeLineQuantity(retainedGift.key, 1);
          cartChanged = true;
        }

        const duplicateGiftLines = giftLines.slice(1);

        if (duplicateGiftLines.length > 0) {
          await removeGiftLines(duplicateGiftLines);
          cartChanged = true;
        }
      }

      if (cartChanged) {
        finishCartUpdate();
      }
    } catch (error) {
      console.error(
        '[Bikini Free Gift] Error al sincronizar el regalo:',
        error
      );
    } finally {
      synchronizationRunning = false;
    }
  }

  function scheduleSynchronization(delay = CONFIG.synchronizationDelay) {
    window.clearTimeout(synchronizationTimer);

    synchronizationTimer = window.setTimeout(function () {
      synchronizeGift();
    }, delay);
  }

  /* =========================================================
     DETECCIÓN DE CAMBIOS DEL CARRITO
  ========================================================= */

  window.fetch = async function (...args) {
    const response = await originalFetch(...args);

    try {
      if (isCartMutation(args[0])) {
        scheduleSynchronization();
      }
    } catch (error) {
      console.error(
        '[Bikini Free Gift] Error en el listener de fetch:',
        error
      );
    }

    return response;
  };

  const originalXhrOpen = XMLHttpRequest.prototype.open;
  const originalXhrSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this.__bikiniGiftRequestUrl = url;
    return originalXhrOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    this.addEventListener('load', function () {
      if (isCartMutation(this.__bikiniGiftRequestUrl)) {
        scheduleSynchronization();
      }
    });

    return originalXhrSend.apply(this, arguments);
  };

  [
    'cart:updated',
    'cart:refresh',
    'cart:change',
    'product:added',
    'ajaxProduct:added'
  ].forEach(function (eventName) {
    document.addEventListener(eventName, function () {
      scheduleSynchronization(150);
    });
  });

  // Revalida al volver mediante el botón Atrás/Adelante del navegador.
  window.addEventListener('pageshow', function () {
    scheduleSynchronization(100);
  });

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') {
      scheduleSynchronization(100);
    }
  });

  // Primera validación al cargar cualquier página.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scheduleSynchronization(50);
    });
  } else {
    scheduleSynchronization(50);
  }
})();
